const DEFAULT_WORKING_HOURS = 8;
const HALF_LEAVE_WORKING_HOURS = 4;
const EARLY_LEAVE_WORKING_HOURS = 6;

/** Leave info for one date (single-user map value or nested user map). */
export function getLeaveInfoForUserDate(leaveByDate, dateStr, userIdStr) {
  const row = leaveByDate?.[dateStr];
  if (!row) return null;
  if (row.kind) return row;
  if (userIdStr) return row[String(userIdStr)] || null;
  return null;
}

export function getRequiredWorkingHours(leaveInfo) {
  if (!leaveInfo) return DEFAULT_WORKING_HOURS;
  if (leaveInfo.kind === "full_leave") return 0;
  if (leaveInfo.kind === "half_leave") return HALF_LEAVE_WORKING_HOURS;
  if (leaveInfo.kind === "early_leave") return EARLY_LEAVE_WORKING_HOURS;
  if (typeof leaveInfo.workingHours === "number") return leaveInfo.workingHours;
  return DEFAULT_WORKING_HOURS;
}

export function isFullLeaveDay(leaveInfo) {
  return leaveInfo?.kind === "full_leave";
}

export function isWfhDay(leaveInfo) {
  return leaveInfo?.kind === "wfh";
}

export function isPartialLeaveDay(leaveInfo) {
  const kind = leaveInfo?.kind;
  return kind === "half_leave" || kind === "early_leave";
}

/** filled | pending | missed — for remaining hours on half / early leave days. */
export function getPartialLeaveComplianceStatus({ isCompliant, isPending }) {
  if (isCompliant) return "filled";
  if (isPending) return "pending";
  return "missed";
}

const PARTIAL_LEAVE_STATUS_LABEL = {
  filled: "Filled",
  pending: "Pending",
  missed: "Missed",
};

/** e.g. "Half day leave · Filled" */
export function getPartialLeaveCalendarTitle(leaveInfo, complianceStatus) {
  const leaveLabel = leaveInfo?.label || "Leave";
  const statusLabel = getPartialLeaveStatusLabel(complianceStatus);
  return `${leaveLabel} · ${statusLabel}`;
}

export function getPartialLeaveStatusLabel(complianceStatus) {
  return (
    PARTIAL_LEAVE_STATUS_LABEL[complianceStatus] ||
    String(complianceStatus || "")
  );
}

export function getTotalTaskMinutesForUser(entries, userIdStr) {
  const list = Array.isArray(entries) ? entries : [];
  const userEntry = userIdStr
    ? list.find((e) => String(e?.userDetails?.userId ?? "") === userIdStr)
    : list[0];
  return (userEntry?.taskDetails || []).reduce(
    (sum, task) => sum + Number(task.duration || 0),
    0
  );
}

/** Whether the day counts as complete for compliance (stats / calendar). */
export function userMeetsDailyWorkRequirement(entries, userIdStr, leaveInfo) {
  if (isFullLeaveDay(leaveInfo)) return true;
  const requiredMinutes = getRequiredWorkingHours(leaveInfo) * 60;
  return getTotalTaskMinutesForUser(entries, userIdStr) >= requiredMinutes;
}

/** Calendar / list title when the day is not yet filled but leave applies. */
export function getLeaveCalendarTitle(leaveInfo) {
  if (!leaveInfo?.label) return "On leave";
  return leaveInfo.label;
}

export function getLeaveBannerMessage(leaveInfo) {
  if (!leaveInfo) return "";
  if (leaveInfo.kind === "wfh") {
    return "You are on approved WFH today. Log your work updates as usual.";
  }
  if (leaveInfo.kind === "full_leave") {
    return "You are on approved leave today. No work update is required.";
  }
  if (leaveInfo.kind === "half_leave") {
    return "You are on approved half day leave. Log work for the remaining hours required below.";
  }
  if (leaveInfo.kind === "early_leave") {
    return "You are on approved early leave. Log work for the remaining hours required below.";
  }
  return `You are on approved leave (${leaveInfo.label}).`;
}
